import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {computeCustomFieldIconName} from '../../../../../helpers/customFieldViewHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {CustomFieldModel} from '../../../../../models/api/customFieldModel';
import {makeGetConversationById} from '../../../../../store/data/conversations/conversationsSelectors';
import {ConversationSummaryContainer} from '../../conversation/conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversation/conversationSummary/conversationSummarySections';
import {ActivityPill} from '../activityTagPill/activityPill';

/*
 * Props.
 */

interface ActivityCustomFieldPillProps {
  conversationId: number;
  customField: CustomFieldModel;
  teammateId: number;
  panelId: ViewerPanelId;
}

/*
 * Style.
 */

const StyledPillWrapperDiv = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 2px;
  vertical-align: middle;
`;

/*
 * Component.
 */

export const ActivityCustomFieldPill: FC<ActivityCustomFieldPillProps> = (props) => {
  const getConversation = useMemo(
    () => makeGetConversationById(props.conversationId),
    [props.conversationId],
  );
  const conversation = useAppSelector(getConversation);

  return (
    <StyledPillWrapperDiv>
      <PopoverCoordinator
        interactionId="activity-custom-field-pill"
        renderButton={(isOpen) => renderPill(isOpen, props)}
        renderModal={(popoverProps) => renderConversationDetails(popoverProps, conversation, props)}
      />
    </StyledPillWrapperDiv>
  );
};

function renderPill(isOpen: boolean, props: ActivityCustomFieldPillProps) {
  const iconName = computeCustomFieldIconName(props.customField.type, VisualSizesEnum.SMALL);

  return (
    <ActivityPill hasHover={isOpen} renderIcon={() => <Icon name={iconName} />}>
      {props.customField.name}
    </ActivityPill>
  );
}

function renderConversationDetails(
  popoverProps: PopoverTransferProps,
  conversation: ConversationModel | undefined,
  props: ActivityCustomFieldPillProps,
) {
  if (!conversation) {
    return null;
  }

  return (
    <ConversationSummaryContainer
      conversation={conversation}
      teammateId={props.teammateId}
      onRequestClose={popoverProps.onRequestClose}
      initiallyExpandedSection={ConversationSummarySectionsEnum.CUSTOM_ATTRIBUTES}
      panelId={props.panelId}
    />
  );
}
